// One browser-side SSE channel: connect, reconnect with backoff, watch for a
// dead stream, and stand down while the tab is hidden.
//
// Every live surface in the app needs exactly this (monitor, admin tags,
// activities) and differs only in its URL and its event type — so the machinery
// lives here once and each feature's client is a thin typed wrapper over it.
//
// Auth is cookie-based: EventSource sends the httpOnly session cookie when
// constructed with `withCredentials`. No token in the URL, and no proactive
// client-side refresh — the backend refreshes upstream tokens server-side and
// emits a `token_expiring` event to prompt a reconnect when needed.

const BFF_BASE_URL = import.meta.env.VITE_BFF_BASE_URL ?? 'http://localhost:3030';

const HEARTBEAT_TIMEOUT_MS = 60_000;
const HIDDEN_CLOSE_DELAY_MS = 5 * 60_000;
const RECONNECT_INITIAL_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

export interface ISseChannelHandlers<E> {
  /** A parsed `message` frame. Malformed JSON is dropped, not surfaced. */
  onEvent: (event: E) => void;
  onTokenExpiring: () => void;
  onOpen: () => void;
  onClose: () => void;
  onError: () => void;
}

/** Absolute URL of a site-scoped stream, e.g. `sitePath(siteId, 'activities/stream')`. */
export function siteStreamUrl(siteId: string, suffix: string): string {
  return `${BFF_BASE_URL}/api/sites/${encodeURIComponent(siteId)}/${suffix}`;
}

/** Absolute URL of a stream that belongs to the signed-in person, not to a site. */
export function userStreamUrl(suffix: string): string {
  return `${BFF_BASE_URL}/api/${suffix}`;
}

export class SseChannel<E> {
  private es: EventSource | null = null;
  private watchdog: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private hiddenCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelayMs = RECONNECT_INITIAL_MS;
  private closed = false;
  private readonly visibilityHandler: () => void;

  constructor(
    private readonly url: string,
    private readonly handlers: ISseChannelHandlers<E>,
  ) {
    this.visibilityHandler = () => this.onVisibilityChange();
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  open(): void {
    // One connection per channel, ever: a second EventSource would orphan the
    // first, which keeps its socket and auto-reconnects forever — enough of them
    // exhaust the browser's six connections per host and every request stalls.
    if (this.closed || this.es) return;
    this.cancelReconnect();
    this.es = new EventSource(this.url, { withCredentials: true });

    this.es.onopen = () => {
      this.reconnectDelayMs = RECONNECT_INITIAL_MS;
      this.handlers.onOpen();
      this.armWatchdog();
    };

    this.es.onmessage = (ev) => {
      this.armWatchdog();
      try {
        this.handlers.onEvent(JSON.parse(ev.data as string) as E);
      } catch {
        // ignore malformed events
      }
    };

    this.es.addEventListener('token_expiring', () => {
      this.handlers.onTokenExpiring();
      this.softClose();
      this.scheduleReconnect();
    });

    this.es.onerror = () => {
      this.handlers.onError();
      this.softClose();
      this.scheduleReconnect();
    };
  }

  close(): void {
    this.closed = true;
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    this.softClose();
    this.cancelReconnect();
    if (this.hiddenCloseTimer) clearTimeout(this.hiddenCloseTimer);
    this.handlers.onClose();
  }

  private softClose(): void {
    if (this.watchdog) clearTimeout(this.watchdog);
    this.watchdog = null;
    this.es?.close();
    this.es = null;
  }

  private armWatchdog(): void {
    if (this.watchdog) clearTimeout(this.watchdog);
    this.watchdog = setTimeout(() => {
      // No event (incl. heartbeat) for 60s — force reconnect.
      this.softClose();
      this.scheduleReconnect();
    }, HEARTBEAT_TIMEOUT_MS);
  }

  private cancelReconnect(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private scheduleReconnect(): void {
    if (this.closed) return;
    this.cancelReconnect();
    const delay = this.reconnectDelayMs;
    this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 2, RECONNECT_MAX_MS);
    this.reconnectTimer = setTimeout(() => this.open(), delay);
  }

  private onVisibilityChange(): void {
    if (document.hidden) {
      if (this.hiddenCloseTimer) clearTimeout(this.hiddenCloseTimer);
      // Standing down means no connection at all: a pending reconnect would
      // reopen it while hidden.
      this.hiddenCloseTimer = setTimeout(() => {
        this.softClose();
        this.cancelReconnect();
      }, HIDDEN_CLOSE_DELAY_MS);
    } else {
      if (this.hiddenCloseTimer) {
        clearTimeout(this.hiddenCloseTimer);
        this.hiddenCloseTimer = null;
      }
      if (!this.es && !this.closed) this.open();
    }
  }
}
